import { forwardRef, memo } from 'react'
import { FIGMA_INPUT_CLASS } from './inputStyles'

/** Shared Figma input. `variant` controls the field shell. */
const Input = forwardRef(function Input({
  variant = 'default',
  type = 'text',
  value,
  defaultValue,
  onChange,
  placeholder = '',
  className = '',
  wrapperClassName = '',
  inputClassName = '',
  onButtonClick,
  buttonText = 'Paste',
  profilePhoto = '',
  profileName = '',
  leading,
  trailing,
  ...props
}, ref) {
  // Keep the previous `type="micro-btn|profile"` API working while new
  // call sites use native HTML `type` plus the visual `variant` prop.
  const legacyVariant = variant === 'default' && ['micro-btn', 'profile'].includes(type) ? type : null
  const resolvedVariant = legacyVariant || variant
  const nativeType = legacyVariant ? 'text' : type
  const isAmount = resolvedVariant === 'amount'
  const isUnstyled = resolvedVariant === 'unstyled'
  const baseClass = [
    isUnstyled ? '' : FIGMA_INPUT_CLASS,
    isAmount ? 'border-0 bg-transparent text-center focus:ring-0' : '',
    leading ? 'pl-11' : '',
    trailing ? 'pr-11' : '',
    inputClassName,
    className,
  ].filter(Boolean).join(' ')

  const input = (
    <input
      ref={ref}
      type={nativeType}
      value={value}
      defaultValue={defaultValue}
      onChange={onChange}
      placeholder={placeholder}
      className={baseClass}
      {...props}
    />
  )

  if (resolvedVariant === 'micro-btn') {
    return (
      <div className={`relative ${wrapperClassName} ${className}`}>
        <input
          ref={ref}
          type={nativeType}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          placeholder={placeholder}
          className={`${FIGMA_INPUT_CLASS} pr-[88px] ${inputClassName}`}
          {...props}
        />
        <button
          type="button"
          onClick={onButtonClick}
          className="absolute right-2 top-1/2 inline-flex h-[38px] -translate-y-1/2 items-center justify-center rounded-[999px] border border-[var(--figma-text-50)] bg-[var(--figma-accent)] px-3 py-0.5 text-[14px] font-[510] leading-5 tracking-[-0.01em] text-[var(--figma-text)] transition-transform duration-[160ms] active:scale-[0.96]"
        >
          {buttonText}
        </button>
      </div>
    )
  }

  if (resolvedVariant === 'profile') {
    return (
      <div className={`relative ${wrapperClassName} ${className}`}>
        <div className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
          {profilePhoto ? (
            <img src={profilePhoto} alt={profileName} className="h-8 w-8 rounded-full object-cover" loading="eager" />
          ) : (
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-[var(--figma-surface-10)]">
              <span className="text-[14px] font-[510] text-[var(--figma-text-50)]">{profileName ? profileName[0]?.toUpperCase() : '?'}</span>
            </div>
          )}
        </div>
        <input
          ref={ref}
          type={nativeType}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          placeholder={placeholder}
          className={`${FIGMA_INPUT_CLASS} pl-[52px] pr-4 ${inputClassName}`}
          {...props}
        />
      </div>
    )
  }

  if (leading || trailing) {
    return (
      <div className={`relative ${wrapperClassName}`}>
        {leading ? <span className="pointer-events-none absolute left-4 top-1/2 z-[1] -translate-y-1/2">{leading}</span> : null}
        {input}
        {trailing ? <span className="pointer-events-none absolute right-4 top-1/2 z-[1] -translate-y-1/2">{trailing}</span> : null}
      </div>
    )
  }

  return input
})

Input.displayName = 'Input'

export default memo(Input)
